import { useState, useEffect, useRef } from "react";
import { X, Check, MapPin, QrCode, ScanFace, Crosshair, CheckCircle2, ArrowLeft, Clock, AlertTriangle, FileText, Camera, ShieldAlert, ShieldCheck, Image as ImageIcon, Trash2 } from "lucide-react";
import { useMotion } from "@/lib/safeMotion";
import { addLateReport } from "@/lib/sharedStore";
import { useFaceVerification } from "@/hooks/useFaceVerification";
import FaceIDScanner from "@/components/app/FaceIDScanner";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

type Step = 1 | 2 | 3;

interface CheckInModalProps {
  checkpoint: { id: string; name: string; zone: string; due?: string; site_id?: string | null; lat?: number | null; lng?: number | null; radius?: number | null };
  onClose: () => void;
  officerFacePhotoUrl?: string | null;
  officerInfo?: { officerId: string; officerName: string; siteId?: string | null };
}

const lateReasons = [
  "Traffic / transport delay",
  "Previous checkpoint took longer",
  "Assisting with an incident",
  "Equipment / system issue",
  "Weather conditions",
  "Instructed by supervisor",
  "Other (please specify)",
];

// Calculate distance between two GPS coordinates in metres (Haversine)
const getDistanceMetres = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const CheckInModal = ({ checkpoint, onClose, officerFacePhotoUrl, officerInfo }: CheckInModalProps) => {
  const { motion, AnimatePresence } = useMotion();
  const [step, setStep] = useState<Step>(1);
  const [method, setMethod] = useState<"gps" | "qr">("gps");
  const [scanDone, setScanDone] = useState(false);
  const [gpsDistance, setGpsDistance] = useState<number | null>(null);
  const [gpsVerified, setGpsVerified] = useState(false);
  const [gpsAccuracy, setGpsAccuracy] = useState<number | null>(null);
  const [userCoords, setUserCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [gpsStatus, setGpsStatus] = useState("Getting your GPS position…");
  const watchIdRef = useRef<number | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [faceVerified, setFaceVerified] = useState(false);
  const [scanningActive, setScanningActive] = useState(false);
  const [checkInSaved, setCheckInSaved] = useState(false);

  // Photo evidence state
  const [checkInPhoto, setCheckInPhoto] = useState<File | null>(null);
  const [checkInPhotoPreview, setCheckInPhotoPreview] = useState<string | null>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);

  const handleCheckInPhoto = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCheckInPhoto(file);
    if (checkInPhotoPreview) URL.revokeObjectURL(checkInPhotoPreview);
    setCheckInPhotoPreview(URL.createObjectURL(file));
  };

  const removeCheckInPhoto = () => {
    if (checkInPhotoPreview) URL.revokeObjectURL(checkInPhotoPreview);
    setCheckInPhoto(null);
    setCheckInPhotoPreview(null);
  };

  // Late check-in state
  const [isLate, setIsLate] = useState(false);
  const [lateMinutes, setLateMinutes] = useState(0);
  const [selectedReason, setSelectedReason] = useState("");
  const [lateDetails, setLateDetails] = useState("");
  const [lateReportSubmitted, setLateReportSubmitted] = useState(false);

  const { 
    isVerifying, 
    scanningState,
    verificationResult, 
    error: verifyError, 
    startCamera, 
    stopCamera, 
    verifyFace, 
    setError: setVerifyError, 
    setVerificationResult 
  } = useFaceVerification();

  // Check if this check-in is late
  useEffect(() => {
    if (checkpoint.due) {
      const now = new Date();
      const [hours, minutes] = checkpoint.due.split(":").map(Number);
      const dueTime = new Date();
      dueTime.setHours(hours, minutes, 0, 0);
      const diffMs = now.getTime() - dueTime.getTime();
      const diffMin = Math.round(diffMs / 60000);
      if (diffMin > 2) {
        setIsLate(true);
        setLateMinutes(diffMin);
      }
    }
  }, [checkpoint.due]);

  const [checkpointPos] = useState<{ lat: number; lng: number } | null>(
    checkpoint.lat != null && checkpoint.lng != null ? { lat: checkpoint.lat, lng: checkpoint.lng } : null
  );
  const checkpointPosRef = useRef<{ lat: number; lng: number } | null>(
    checkpoint.lat != null && checkpoint.lng != null ? { lat: checkpoint.lat, lng: checkpoint.lng } : null
  );
  const geofenceRadius = checkpoint.radius || 25;

  // Cleanup camera on unmount
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, [stopCamera]);

  // Start camera when entering face scan
  const handleStartScan = async () => {
    setScanningActive(true);
    setVerifyError(null);
    setVerificationResult(null);

    // Wait for next render tick so video element is mounted
    await new Promise(r => setTimeout(r, 50));

    if (videoRef.current) {
      const success = await startCamera(videoRef.current);
      if (success) {
        // Wait for video to be ready
        videoRef.current.onloadeddata = () => setCameraReady(true);
      } else {
        setScanningActive(false);
      }
    }
  };

  // Track failed state for retry
  const [verifyFailed, setVerifyFailed] = useState(false);
  const [failMessage, setFailMessage] = useState("");

  // Capture and verify face
  const handleCaptureFace = async () => {
    if (!officerFacePhotoUrl) {
      toast.error("Cannot check in: No reference photo on file. Your supervisor must register your face photo before you can check in.");
      return;
    }

    setVerifyFailed(false);
    setFailMessage("");

    const result = await verifyFace(officerFacePhotoUrl, officerInfo);
    
    // Show result in scanner for 2s before transitioning
    setTimeout(() => {
      if (result.match && result.liveness && result.confidence >= 70) {
        setFaceVerified(true);
        setScanDone(true);
        setScanningActive(false);
        stopCamera();
        toast.success(`Face verified (${result.confidence}% confidence)`);
        setStep(2);
      } else {
        setVerifyFailed(true);
        setFailMessage(result.reason || "Face verification failed.");
      }
    }, 2000);
  };

  // Retry verification without restarting camera
  const handleRetryVerification = () => {
    setVerifyFailed(false);
    setFailMessage("");
    setVerificationResult(null);
    setVerifyError(null);
  };

  // Real GPS tracking for geofence check-in
  useEffect(() => {
    if (step !== 2 || method !== "gps") return;
    let fallbackTimer: ReturnType<typeof setInterval> | null = null;

    const runDemoSimulation = (statusMsg: string) => {
      setGpsStatus(statusMsg);
      let simDistance = 45;
      fallbackTimer = setInterval(() => {
        simDistance = Math.max(0, simDistance - Math.random() * 12);
        const rounded = Math.round(simDistance);
        setGpsDistance(rounded);
        if (rounded <= 5) {
          setGpsVerified(true);
          setGpsStatus(`You are at ${checkpoint.name}!`);
          if (fallbackTimer) clearInterval(fallbackTimer);
        }
      }, 800);
    };

    if (!navigator.geolocation) {
      runDemoSimulation("GPS unavailable — running simulation");
      return () => { if (fallbackTimer) clearInterval(fallbackTimer); };
    }

    setGpsStatus("Getting your GPS position…");

    const handlePosition = (pos: GeolocationPosition) => {
      const lat = pos.coords.latitude;
      const lng = pos.coords.longitude;
      setUserCoords({ lat, lng });
      setGpsAccuracy(Math.round(pos.coords.accuracy));

      if (!checkpointPosRef.current) {
        // No checkpoint coordinates — auto-verify GPS (fallback)
        setGpsVerified(true);
        setGpsStatus(`GPS verified at your current position`);
        return;
      }

      const cpPos = checkpointPosRef.current;
      const dist = getDistanceMetres(lat, lng, cpPos.lat, cpPos.lng);
      const rounded = Math.round(dist);
      setGpsDistance(rounded);

      if (rounded <= geofenceRadius) {
        setGpsVerified(true);
        setGpsStatus(`You are at ${checkpoint.name}!`);
      } else {
        setGpsStatus(`${rounded}m away — move to within ${geofenceRadius}m`);
      }
    };

    const handleError = (err: GeolocationPositionError) => {
      console.warn("GPS error:", err.message);
      runDemoSimulation("GPS permission denied — running simulation");
    };

    const geoOptions: PositionOptions = { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 };
    const watchId = navigator.geolocation.watchPosition(handlePosition, handleError, geoOptions);
    watchIdRef.current = watchId;

    return () => {
      navigator.geolocation.clearWatch(watchId);
      if (fallbackTimer) clearInterval(fallbackTimer);
    };
  }, [step, method, checkpoint.name]);

  const stepLabels = [
    { num: "1", label: "Face Scan", icon: ScanFace },
    { num: "2", label: method === "gps" ? "GPS" : "QR", icon: method === "gps" ? Crosshair : QrCode },
    { num: "3", label: "Complete", icon: CheckCircle2 },
  ];

  // Save check-in to database
  const saveCheckIn = async () => {
    if (checkInSaved || !officerInfo) return;
    try {
      let photoUrl: string | null = null;
      if (checkInPhoto) {
        const filePath = `${officerInfo.officerId}/${Date.now()}-checkin.jpg`;
        const { error: uploadErr } = await supabase.storage.from("incident-photos").upload(filePath, checkInPhoto, { contentType: checkInPhoto.type });
        if (!uploadErr) {
          const { data: urlData } = supabase.storage.from("incident-photos").getPublicUrl(filePath);
          photoUrl = urlData.publicUrl;
        }
      }

      const now = new Date();
      await supabase.from("check_ins").insert({
        officer_id: officerInfo.officerId,
        site_id: checkpoint.site_id || null,
        checkpoint_id: checkpoint.id,
        checked_at: now.toISOString(),
        method: method,
        coords: userCoords ? { lat: userCoords.lat, lng: userCoords.lng } : null,
        on_time: !isLate,
        variance_minutes: isLate ? lateMinutes : 0,
        scheduled_at: checkpoint.due ? (() => { const d = new Date(); const [h, m] = checkpoint.due!.split(":").map(Number); d.setHours(h, m, 0, 0); return d.toISOString(); })() : null,
        photo_url: photoUrl,
      });
      setCheckInSaved(true);
    } catch (err) {
      console.error("Failed to save check-in:", err);
      toast.error("Failed to save check-in to database");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[hsl(var(--app-bg)/0.97)] backdrop-blur-sm font-outfit">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-md mx-4 app-card rounded-2xl overflow-hidden max-h-[92vh] flex flex-col shadow-2xl border border-[hsl(var(--app-border-subtle))]">
        {/* Header - Fixed at top */}
        <div className="shrink-0 p-4 flex items-center justify-between border-b border-[hsl(var(--app-border-subtle))] app-surface-1 z-10 sticky top-0">
          <button onClick={() => { stopCamera(); onClose(); }} className="p-1.5 rounded-lg text-[hsl(var(--app-text-muted))] hover:text-[hsl(var(--app-text-secondary))] hover:bg-[hsl(var(--app-surface-2))] transition-colors">
            <X className="w-5 h-5" />
          </button>
          <div className="text-center min-w-0 px-2">
            <p className="font-bebas text-xl text-[hsl(var(--app-text-primary))] truncate">{checkpoint.name}</p>
            <p className="text-[10px] text-[hsl(var(--app-text-muted))] truncate">{checkpoint.zone}</p>
          </div>
          <div className="w-8 shrink-0" />
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-center gap-2 py-4 px-4">
          {stepLabels.map((s, i) => {
            const isActive = step === i + 1;
            const isDone = step > i + 1;
            const Icon = s.icon;
            return (
              <div key={i} className="flex items-center gap-2">
                <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                  isDone ? "border-[hsl(var(--app-green))] bg-[hsl(var(--app-green)/0.08)] text-[hsl(var(--app-green))]" :
                  isActive ? "border-[hsl(var(--app-gold))] bg-[hsl(var(--app-gold)/0.08)] text-[hsl(var(--app-gold))] gold-glow" :
                  "border-[hsl(var(--app-border-subtle))] text-[hsl(var(--app-text-muted))]"
                }`}>
                  {isDone ? <CheckCircle2 className="w-3 h-3" /> : <Icon className="w-3 h-3" />}
                  {s.label}
                </span>
                {i < 2 && <span className="text-[hsl(var(--app-border-active))]">›</span>}
              </div>
            );
          })}
        </div>

        {/* Content area - Scrollable */}
        <div className="flex-1 overflow-y-auto p-5 sm:p-6 custom-scrollbar">
          <AnimatePresence mode="wait">
            {step === 1 && !scanningActive && (
              <motion.div key="step1" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
                <p className="text-sm text-[hsl(var(--app-text-secondary))] text-center">Verify your identity with a live face scan, then check in.</p>
                
                <div className="grid grid-cols-2 gap-3">
                  <button onClick={() => setMethod("gps")} className={`p-4 rounded-xl border-2 text-center transition-all ${
                    method === "gps" ? "border-[hsl(var(--app-gold))] bg-[hsl(var(--app-gold)/0.06)] text-[hsl(var(--app-gold))]" : "border-[hsl(var(--app-border-subtle))] text-[hsl(var(--app-text-muted))] hover:border-[hsl(var(--app-border-active))]"
                  }`}>
                    <MapPin className="w-8 h-8 mx-auto mb-2" />
                    <p className="text-sm font-semibold">GPS Geofence</p>
                    <p className="text-[10px] mt-1 opacity-70">No hardware needed</p>
                  </button>
                  <button onClick={() => setMethod("qr")} className={`p-4 rounded-xl border-2 text-center transition-all ${
                    method === "qr" ? "border-[hsl(var(--app-gold))] bg-[hsl(var(--app-gold)/0.06)] text-[hsl(var(--app-gold))]" : "border-[hsl(var(--app-border-subtle))] text-[hsl(var(--app-text-muted))] hover:border-[hsl(var(--app-border-active))]"
                  }`}>
                    <QrCode className="w-8 h-8 mx-auto mb-2" />
                    <p className="text-sm font-semibold">QR Code Scan</p>
                    <p className="text-[10px] mt-1 opacity-70">Scan at checkpoint</p>
                  </button>
                </div>

                {!officerFacePhotoUrl && (
                  <div className="p-3 rounded-xl bg-[hsl(var(--app-red)/0.08)] border border-[hsl(var(--app-red)/0.2)] flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-[hsl(var(--app-red))] shrink-0 mt-0.5" />
                    <p className="text-xs text-[hsl(var(--app-red))]">Cannot check in: No reference photo on file. Your supervisor must register your face photo first.</p>
                  </div>
                )}

                <button onClick={handleStartScan} disabled={!officerFacePhotoUrl} className="w-full py-4 rounded-xl bg-[hsl(var(--app-purple))] text-white font-semibold text-sm hover:brightness-110 transition-all flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
                  <Camera className="w-5 h-5" /> Begin Live Face Verification
                </button>
                <p className="text-[10px] text-center text-[hsl(var(--app-text-muted))]">Anti-spoofing enabled — printed photos, screens &amp; masks will be rejected</p>
              </motion.div>
            )}

            {step === 1 && scanningActive && (
              <motion.div key="scanning" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
                <p className="font-bebas text-lg text-center text-[hsl(var(--app-text-primary))]">LIVE FACE VERIFICATION</p>
                
                {/* Live camera feed */}
                <FaceIDScanner 
                  videoRef={videoRef} 
                  state={scanningState} 
                  error={verifyError} 
                />

                {/* Verification result feedback */}
                {verificationResult && !verificationResult.match && !verifyFailed && (
                  <div className="p-3 rounded-xl bg-[hsl(var(--app-red)/0.08)] border border-[hsl(var(--app-red)/0.2)] flex items-start gap-2">
                    <ShieldAlert className="w-4 h-4 text-[hsl(var(--app-red))] shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-semibold text-[hsl(var(--app-red))]">Verification failed ({verificationResult.confidence}%)</p>
                      <p className="text-[10px] text-[hsl(var(--app-text-muted))] mt-0.5">{verificationResult.reason}</p>
                    </div>
                  </div>
                )}

                {/* Retry state — shows error + retry/cancel buttons */}
                {verifyFailed && (
                  <div className="p-3 rounded-xl bg-[hsl(var(--app-red)/0.08)] border-2 border-[hsl(var(--app-red)/0.3)] space-y-2">
                    <div className="flex items-start gap-2">
                      <ShieldAlert className="w-4 h-4 text-[hsl(var(--app-red))] shrink-0 mt-0.5" />
                      <p className="text-xs font-semibold text-[hsl(var(--app-red))]">{failMessage}</p>
                    </div>
                    <p className="text-[10px] text-[hsl(var(--app-text-muted))]">Adjust lighting, remove obstructions, and try again.</p>
                  </div>
                )}

                {verifyError && !verificationResult && !verifyFailed && (
                  <p className="text-xs text-center text-[hsl(var(--app-red))]">{verifyError}</p>
                )}

                <p className="text-sm text-center text-[hsl(var(--app-text-secondary))]">
                  {isVerifying ? "Verifying identity & liveness..." : verifyFailed ? "Ready to retry — position your face and tap Retry" : cameraReady ? "Position your face in the circle, then tap capture. Two frames will be taken for liveness check." : "Starting camera..."}
                </p>

                <div className="flex gap-3">
                  <button
                    onClick={() => { setScanningActive(false); stopCamera(); setCameraReady(false); setVerifyFailed(false); setFailMessage(""); }}
                    className="flex-1 py-3 text-sm text-[hsl(var(--app-text-muted))] border border-[hsl(var(--app-border-subtle))] rounded-xl hover:text-[hsl(var(--app-text-secondary))] transition-colors"
                  >
                    Cancel
                  </button>
                  {verifyFailed ? (
                    <button
                      onClick={handleRetryVerification}
                      className="flex-1 py-3 rounded-xl bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] font-semibold text-sm hover:brightness-110 transition-all flex items-center justify-center gap-2"
                    >
                      <Camera className="w-4 h-4" /> Retry Verification
                    </button>
                  ) : (
                    <button
                      onClick={handleCaptureFace}
                      disabled={!cameraReady || isVerifying}
                      className="flex-1 py-3 rounded-xl bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] font-semibold text-sm hover:brightness-110 transition-all flex items-center justify-center gap-2 disabled:opacity-40"
                    >
                      <Camera className="w-4 h-4" /> {isVerifying ? "Verifying..." : "Capture & Verify"}
                    </button>
                  )}
                </div>
              </motion.div>
            )}

            {step === 2 && method === "gps" && (
              <motion.div key="gps" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-6 text-center">
                {/* Face verified badge */}
                <div className="flex items-center justify-center gap-2 p-2 rounded-xl bg-[hsl(var(--app-green)/0.06)] border border-[hsl(var(--app-green)/0.15)]">
                  <ShieldCheck className="w-4 h-4 text-[hsl(var(--app-green))]" />
                  <span className="text-xs text-[hsl(var(--app-green))] font-medium">Identity Verified {verificationResult?.confidence ? `(${verificationResult.confidence}%)` : ""}</span>
                </div>

                <div className="relative w-[120px] h-[120px] mx-auto">
                  <div className={`w-full h-full rounded-full border-[3px] flex flex-col items-center justify-center transition-colors ${
                    gpsVerified ? "border-[hsl(var(--app-green))] green-glow" : (gpsDistance !== null && gpsDistance < geofenceRadius * 2) ? "border-[hsl(var(--app-gold))] gold-glow" : "border-[hsl(var(--app-border-active))]"
                  }`}>
                    <span className="font-bebas text-3xl text-[hsl(var(--app-text-primary))]">{gpsDistance ?? "…"}</span>
                    <span className="text-[10px] text-[hsl(var(--app-text-muted))]">metres away</span>
                  </div>
                  {gpsVerified && (
                    <>
                      <div className="absolute inset-[-8px] rounded-full border-2 border-[hsl(var(--app-gold)/0.3)] animate-ping" />
                      <div className="absolute inset-[-16px] rounded-full border border-[hsl(var(--app-gold)/0.15)] animate-ping" style={{ animationDelay: '0.5s' }} />
                    </>
                  )}
                </div>

                <div className="h-2 rounded-full bg-[hsl(var(--app-border-subtle))] overflow-hidden">
                  <motion.div className={`h-full rounded-full ${gpsVerified ? "bg-[hsl(var(--app-green))]" : "bg-[hsl(var(--app-gold))]"}`} 
                    animate={{ width: `${gpsDistance !== null ? Math.max(5, 100 - (gpsDistance / (geofenceRadius * 2)) * 100) : 5}%` }} />
                </div>

                <p className={`text-sm font-medium ${gpsVerified ? "text-[hsl(var(--app-green))]" : "text-[hsl(var(--app-text-secondary))]"}`}>
                  {gpsVerified && <CheckCircle2 className="w-4 h-4 inline mr-1" />}{gpsStatus}
                </p>

                {userCoords && (
                  <div className="p-3 rounded-xl bg-[hsl(var(--app-surface-2))] border border-[hsl(var(--app-border-subtle))] space-y-1">
                    <div className="flex items-center justify-center gap-1.5 text-xs font-mono-data text-[hsl(var(--app-text-muted))]">
                      <MapPin className="w-3 h-3 text-[hsl(var(--app-gold))]" />
                      {userCoords.lat.toFixed(6)}, {userCoords.lng.toFixed(6)}
                    </div>
                    {gpsAccuracy && <p className="text-[10px] text-[hsl(var(--app-text-muted))]">Accuracy: ±{gpsAccuracy}m</p>}
                  </div>
                )}

                <p className="text-xs font-mono-data text-[hsl(var(--app-text-muted))]">Must be within {geofenceRadius}m of {checkpoint.name}</p>

                {gpsVerified && (
                  <>
                    {/* Photo evidence */}
                    <div className="p-3 rounded-xl border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2)/0.5)] space-y-2">
                      <div className="flex items-center gap-2">
                        <Camera className="w-3.5 h-3.5 text-[hsl(var(--app-text-muted))]" />
                        <p className="text-[10px] font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider">Photo Evidence (Recommended)</p>
                      </div>
                      <input ref={photoInputRef} type="file" accept="image/*" capture="environment" onChange={handleCheckInPhoto} className="hidden" />
                      {checkInPhotoPreview ? (
                        <div className="relative w-full h-32 rounded-lg overflow-hidden border border-[hsl(var(--app-border-subtle))]">
                          <img src={checkInPhotoPreview} alt="Check-in photo" className="w-full h-full object-cover" />
                          <button onClick={removeCheckInPhoto} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-[hsl(var(--app-bg)/0.8)] text-[hsl(var(--app-red))] flex items-center justify-center">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => photoInputRef.current?.click()} className="w-full py-3 rounded-xl border border-dashed border-[hsl(var(--app-gold)/0.3)] text-xs text-[hsl(var(--app-gold))] hover:bg-[hsl(var(--app-gold)/0.05)] transition-colors flex items-center justify-center gap-1.5">
                          <Camera className="w-3.5 h-3.5" /> Take Photo at Checkpoint
                        </button>
                      )}
                    </div>

                    <button onClick={() => setStep(3)} className="w-full py-4 rounded-xl bg-[hsl(var(--app-green))] text-white font-semibold hover:brightness-110 transition-all flex items-center justify-center gap-2">
                      <CheckCircle2 className="w-5 h-5" /> Location Confirmed — Complete Check-In
                    </button>
                  </>
                )}
              </motion.div>
            )}

            {step === 2 && method === "qr" && (
              <motion.div key="qr" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-6 text-center">
                {/* Face verified badge */}
                <div className="flex items-center justify-center gap-2 p-2 rounded-xl bg-[hsl(var(--app-green)/0.06)] border border-[hsl(var(--app-green)/0.15)]">
                  <ShieldCheck className="w-4 h-4 text-[hsl(var(--app-green))]" />
                  <span className="text-xs text-[hsl(var(--app-green))] font-medium">Identity Verified {verificationResult?.confidence ? `(${verificationResult.confidence}%)` : ""}</span>
                </div>

                <div className="w-[180px] h-[180px] mx-auto bg-white rounded-xl p-4 flex items-center justify-center">
                  <QrCode className="w-full h-full text-[hsl(var(--app-bg))]" />
                </div>
                <p className="text-sm text-[hsl(var(--app-text-secondary))]">Scan the QR code at {checkpoint.name}</p>
                
                {/* Photo evidence for QR method */}
                <div className="p-3 rounded-xl border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2)/0.5)] space-y-2 text-left">
                  <div className="flex items-center gap-2">
                    <Camera className="w-3.5 h-3.5 text-[hsl(var(--app-text-muted))]" />
                    <p className="text-[10px] font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider">Photo Evidence (Recommended)</p>
                  </div>
                  <input ref={photoInputRef} type="file" accept="image/*" capture="environment" onChange={handleCheckInPhoto} className="hidden" />
                  {checkInPhotoPreview ? (
                    <div className="relative w-full h-32 rounded-lg overflow-hidden border border-[hsl(var(--app-border-subtle))]">
                      <img src={checkInPhotoPreview} alt="Check-in photo" className="w-full h-full object-cover" />
                      <button onClick={removeCheckInPhoto} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-[hsl(var(--app-bg)/0.8)] text-[hsl(var(--app-red))] flex items-center justify-center">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <button onClick={() => photoInputRef.current?.click()} className="w-full py-3 rounded-xl border border-dashed border-[hsl(var(--app-gold)/0.3)] text-xs text-[hsl(var(--app-gold))] hover:bg-[hsl(var(--app-gold)/0.05)] transition-colors flex items-center justify-center gap-1.5">
                      <Camera className="w-3.5 h-3.5" /> Take Photo at Checkpoint
                    </button>
                  )}
                </div>

                <button onClick={() => setStep(3)} className="w-full py-4 rounded-xl bg-[hsl(var(--app-green))] text-white font-semibold hover:brightness-110 transition-all flex items-center justify-center gap-2">
                  <CheckCircle2 className="w-5 h-5" /> Confirm QR Scan
                </button>
              </motion.div>
            )}

            {step === 3 && isLate && !lateReportSubmitted && (
              <motion.div key="late-report" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
                <div className="flex items-center gap-3 p-3 rounded-xl bg-[hsl(var(--app-gold)/0.08)] border border-[hsl(var(--app-gold)/0.3)]">
                  <AlertTriangle className="w-5 h-5 text-[hsl(var(--app-gold))] shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-[hsl(var(--app-gold))]">Late Check-In — +{lateMinutes}min</p>
                    <p className="text-[10px] text-[hsl(var(--app-text-muted))]">Due {checkpoint.due} · Checked in {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                </div>

                <div>
                  <p className="text-xs font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider mb-2">Reason for late check-in *</p>
                  <div className="space-y-2">
                    {lateReasons.map((reason) => (
                      <button
                        key={reason}
                        onClick={() => setSelectedReason(reason)}
                        className={`w-full text-left px-4 py-3 rounded-xl border text-sm transition-all ${
                          selectedReason === reason
                            ? "border-[hsl(var(--app-gold))] bg-[hsl(var(--app-gold)/0.06)] text-[hsl(var(--app-gold))]"
                            : "border-[hsl(var(--app-border-subtle))] text-[hsl(var(--app-text-secondary))] hover:border-[hsl(var(--app-border-active))]"
                        }`}
                      >
                        {reason}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-xs font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider mb-2">Additional details {selectedReason === "Other (please specify)" ? "*" : "(optional)"}</p>
                  <textarea
                    value={lateDetails}
                    onChange={(e) => setLateDetails(e.target.value.slice(0, 300))}
                    placeholder="Provide additional context for the late check-in..."
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-sm text-[hsl(var(--app-text-primary))] placeholder:text-[hsl(var(--app-text-muted))] outline-none focus:border-[hsl(var(--app-gold))] resize-none font-outfit transition-colors"
                  />
                  <p className="text-[10px] text-[hsl(var(--app-text-muted))] text-right mt-1 font-mono-data">{lateDetails.length}/300</p>
                </div>

                <button
                  onClick={() => {
                    if (!selectedReason) return;
                    if (selectedReason === "Other (please specify)" && !lateDetails.trim()) return;
                    addLateReport({
                      id: `lr-${Date.now()}`,
                      officerId: "current",
                      officerName: "Current Officer",
                      checkpoint: checkpoint.name,
                      site: checkpoint.zone || "",
                      reason: selectedReason,
                      details: lateDetails,
                      lateMinutes: lateMinutes,
                      timestamp: new Date().toISOString(),
                    });
                    setLateReportSubmitted(true);
                  }}
                  disabled={!selectedReason || (selectedReason === "Other (please specify)" && !lateDetails.trim())}
                  className="w-full py-4 rounded-xl bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] font-semibold hover:brightness-110 transition-all flex items-center justify-center gap-2 disabled:opacity-40"
                >
                  <FileText className="w-4 h-4" /> Submit Late Report & Complete
                </button>
              </motion.div>
            )}

            {step === 3 && (!isLate || lateReportSubmitted) && (
              <motion.div key="done" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} onAnimationComplete={saveCheckIn} className="text-center space-y-4 py-4">
                <div className="w-16 h-16 rounded-full bg-[hsl(var(--app-green)/0.12)] flex items-center justify-center mx-auto">
                  <CheckCircle2 className="w-8 h-8 text-[hsl(var(--app-green))]" />
                </div>
                <h3 className="font-bebas text-2xl text-[hsl(var(--app-green))]">CHECK-IN VERIFIED</h3>
                <p className="text-sm text-[hsl(var(--app-text-secondary))]">{checkpoint.name} · {new Date().toLocaleTimeString()}</p>
                <div className="flex items-center justify-center gap-3 text-sm text-[hsl(var(--app-text-secondary))]">
                  <span className="inline-flex items-center gap-1 text-[hsl(var(--app-green))]"><ScanFace className="w-3.5 h-3.5" /> Face {verificationResult?.confidence ? `${verificationResult.confidence}%` : "✓"}</span>
                  <span className="text-[hsl(var(--app-border-active))]">›</span>
                  <span className="inline-flex items-center gap-1 text-[hsl(var(--app-green))]">{method === "gps" ? <MapPin className="w-3.5 h-3.5" /> : <QrCode className="w-3.5 h-3.5" />} {method === "gps" ? "GPS" : "QR"}</span>
                  <span className="text-[hsl(var(--app-border-active))]">›</span>
                  <span className="inline-flex items-center gap-1 text-[hsl(var(--app-green))]"><CheckCircle2 className="w-3.5 h-3.5" /> Logged</span>
                </div>

                {isLate && lateReportSubmitted && (
                  <div className="p-3 rounded-xl bg-[hsl(var(--app-gold)/0.06)] border border-[hsl(var(--app-gold)/0.15)]">
                    <p className="text-xs text-[hsl(var(--app-gold))] flex items-center justify-center gap-1"><Clock className="w-3 h-3" /> Late report submitted — {selectedReason}</p>
                  </div>
                )}

                <div className="p-3 rounded-xl bg-[hsl(var(--app-green)/0.06)] border border-[hsl(var(--app-green)/0.15)]">
                  <p className="text-xs text-[hsl(var(--app-green))]">Check-in recorded and synced successfully</p>
                </div>
                <button onClick={onClose} className="w-full py-3 rounded-xl border border-[hsl(var(--app-gold)/0.3)] text-[hsl(var(--app-gold))] font-semibold hover:bg-[hsl(var(--app-gold)/0.05)] transition-colors flex items-center justify-center gap-1.5">
                  <ArrowLeft className="w-4 h-4" /> Back to Patrol
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

export default CheckInModal;
